import React from "react";
import type { SanityRichTextValue } from "@ouster/shared";
import { RichText } from "~/features/ui/rich-text";
import { toCraftRichText } from "./to-html";
import { SanityRichTextMediaBlock, SanityRichTextMediaGridBlock, SanityRichTextMediaSliderBlock, SanityRichTextQuoteBlock } from "./blocks";
type Props = Omit<React.ComponentPropsWithRef<"div">, "children"> & {
  value?: SanityRichTextValue | null;
};
type PortableTextNode = {
  _key?: string;
  _type?: string;
};
function renderRichTextBlock(block: PortableTextNode) {
  switch (block._type) {
    case "wild.richtext.block.mediaOnly":
      return <SanityRichTextMediaBlock value={block} />;
    case "wild.richtext.block.mediaGrid":
      return <SanityRichTextMediaGridBlock value={block} />;
    case "wild.richtext.block.mediaSlider":
      return <SanityRichTextMediaSliderBlock value={block} />;
    case "wild.richtext.block.quote":
      return <SanityRichTextQuoteBlock value={block} />;
    default:
      return null;
  }
}
export function SanityRichText({
  value,
  ...rest
}: Props) {
  if (!Array.isArray(value)) {
    return null;
  }
  const hasCustomBlocks = value.some(block => {
    const type = (block as PortableTextNode)._type;
    return typeof type === "string" && type !== "block";
  });
  if (!hasCustomBlocks) {
    const text = toCraftRichText(value);
    if (!text) {
      return null;
    }
    return <RichText {...rest} text={text} />;
  }
  const children: React.ReactNode[] = [];
  let textBlocks: PortableTextNode[] = [];
  function flushTextBlocks() {
    const text = toCraftRichText(textBlocks as SanityRichTextValue);
    if (text) {
      children.push(<RichText key={`text-${children.length}`} text={text} />);
    }
    textBlocks = [];
  }
  for (const block of value as PortableTextNode[]) {
    if (block._type === "block") {
      textBlocks.push(block);
      continue;
    }
    flushTextBlocks();
    const renderedBlock = renderRichTextBlock(block);
    if (renderedBlock) {
      children.push(<React.Fragment key={block._key || `${block._type}-${children.length}`}>{renderedBlock}</React.Fragment>);
    }
  }
  flushTextBlocks();
  if (!children.length) {
    return null;
  }
  return <div {...rest}>{children}</div>;
}